import React from 'react';
import { appLogger } from '../utils/logger';

// Error Boundary State Type
interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
}

// Error Boundary Component
export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error: Error) {
        appLogger.error('Error caught by boundary', error);
        return {
            hasError: true,
            error: error,
            errorInfo: null
        };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        appLogger.error('React component error', { error, errorInfo });
        this.setState({
            error,
            errorInfo
        });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 text-gray-900 flex items-center justify-center p-4">
                    <div className="bg-white p-6 rounded-lg max-w-2xl w-full border border-orange-200 shadow-lg">
                        <h2 className="text-2xl font-bold text-red-500 mb-4">
                            {this.state.error?.name || 'Application Error'}
                        </h2>

                        <div className="mb-6 p-4 bg-slate-700 rounded">
                            <p className="font-medium mb-2">{this.state.error?.message || 'An unknown error occurred'}</p>

                            {this.state.error?.stack && (
                                <details className="mt-2 text-sm text-slate-300">
                                    <summary className="cursor-pointer text-blue-400">View technical details</summary>
                                    <pre className="mt-2 p-2 bg-slate-800 rounded overflow-auto max-h-40">
                                        {this.state.error.stack}
                                    </pre>
                                </details>
                            )}

                            {this.state.errorInfo?.componentStack && (
                                <details className="mt-2 text-sm text-slate-300">
                                    <summary className="cursor-pointer text-blue-400">Component stack</summary>
                                    <pre className="mt-2 p-2 bg-slate-800 rounded overflow-auto max-h-40">
                                        {this.state.errorInfo.componentStack}
                                    </pre>
                                </details>
                            )}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={() => window.location.reload()}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
                            >
                                Refresh Page
                            </button>

                            {this.state.error && (
                                <button
                                    onClick={() => {
                                        const errorDetails = `Error: ${this.state.error?.toString()}\n\n` +
                                            `Stack: ${this.state.error?.stack || 'No stack trace'}\n\n` +
                                            `Component Stack: ${this.state.errorInfo?.componentStack || 'No component stack'}`;
                                        navigator.clipboard.writeText(errorDetails);
                                        alert('Error details copied to clipboard');
                                    }}
                                    className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-medium py-2 px-4 rounded transition-colors"
                                >
                                    Copy Error Details
                                </button>
                            )}
                        </div>

                        <p className="mt-4 text-sm text-slate-400">
                            If the problem persists, please contact support with the error details above.
                        </p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
